/**
 * guest-issue-add — the guest-order list's own "Add guest" trigger opens this
 * behind schmancy's show(): a ticket-type-row list + name/email + locale
 * chips + an Issue button. Zero I/O — it closes with a plain GuestAddDetail
 * on Issue, or nothing on Esc/backdrop dismiss (schmancy's own overlay
 * contract, per overlay.md — no in-dialog Cancel needed). guest-issue.view.ts
 * is the one caller that ever fires guestIssueAction$; this dialog never
 * imports guest-issue.state.ts's write channel, only its line-validity rules
 * (guestIssueLinesValid/clampGuestIssueQty/fitGuestIssueQty), pinned to the
 * same two bounds the guard observes — the guest-create write rule's line
 * count, and settleCheckout's own total-qty ceiling.
 *
 * Ported from the designer studio's already-reviewed, since-retired proof per
 * FAITHFUL_REALIZATION — same ticket-type-row + qty-stepper shape, now typed
 * against guest-issue.state.ts's own GuestIssueLine/GuestIssueBuyer rather
 * than the studio's own GuestLine duplicate.
 *
 * Each row's qty numeral is typable here and nowhere else (the row's own
 * `qtyField` opt-in): a guest issuance runs to hundreds, and tapping `+` a
 * hundred and fifty times is not a quantity entry. A blank field and a typed
 * zero are the same intent — the type is not selected, the key dropped,
 * exactly where `−` already lands — so the two affordances can never disagree
 * about what an empty-looking row means; anything above zero goes through
 * clampGuestIssueQty to the write rule's own `qty >= 1` floor and then through
 * fitGuestIssueQty to the headroom Event.MAX_TICKETS_PER_ORDER leaves the
 * other rows, so neither a keystroke nor a `+` composes a line the write rule
 * refuses or a total settleCheckout refuses. At the ceiling every `+` goes
 * quiet and the band under the list names the number.
 *
 * The day row is the storefront's own <programme-day-selector>, handed the
 * days this catalog admits on, the day in force, and the days this draft
 * already holds a quantity on — the same dot the buyer's basket lights.
 * Switching days never touches the draft, so on a multi-day guest order that
 * dot is the only map of what has already been filled in.
 *
 * The list is grouped the way the shop's own event page groups it, because an
 * operator issuing a comp is reading the same catalog the buyer is: the types
 * tied to no single day stand once ABOVE the chip row, under the heading that
 * names what they are (the shop's own storefront.typeSelector.wholeEventTitle
 * — one string for one concept, whichever surface renders it), and the list
 * below the chips holds only the selected day's own types. Merging the two
 * into one flat list under the chips is what this used to do, and it says a
 * festival pass is good for the day currently selected. Where the catalog has
 * no day-bound type there is no day to be outside of, so nothing stands above
 * and every type is simply a row — the flat list, unchanged to the pixel.
 *
 * The order cap is named once, under the day's list: the ceiling is on the
 * whole draft, not on either section, so a second band above the chips would
 * be the same sentence twice about one number.
 *
 * The horizontal padding rides each section rather than the root column, so
 * the chip row is full-bleed and its gutter lands on the chips' own scroller
 * (`inline-padding="px-5"`). Eight calendar pills are wider than a `max-w-md`
 * overlay; with the padding on the root, the row terminates 20px short of
 * both edges and reads as a boxed cut rather than as content running on.
 *
 * No `<schmancy-surface>` root: the overlay itself already supplies the
 * glass/depth material, so this mounts a plain `<div>`, never a second opaque
 * fill layered over the overlay's own translucent one.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { formatCurrency } from "@mhmo91/rx";
import { type Locale, LocaleEnum, resolveLocalizedText } from "@momo/i18n";
import { Checkout, Event, TicketType } from "@momo/types";
import { html, type PropertyValues } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import "../../storefront/programme-day-selector.view.ts";
import type { TicketTypeRow } from "../../storefront/ticket-type-row.view.ts";
import "../../storefront/ticket-type-row.view.ts";
import {
	clampGuestIssueQty,
	fitGuestIssueQty,
	type GuestIssueLine,
	guestIssueLinesValid,
} from "./guest-issue.state.ts";
import type { SaleEventOption, SaleTicketType } from "./sale.state.ts";

/** DE/EN — LocaleEnum's own two-value set, shown as full endonyms (not the
 *  bare code) since the chip IS the only "which language does this guest's
 *  ticket print in" affordance in this flow. A language's own name in its
 *  own language is not translated. */
const LOCALE_CHIP_LABEL: Record<Locale, string> = {
	de: "Deutsch",
	en: "English",
};

export type GuestAddDetail = {
	readonly name: string;
	readonly email: string;
	readonly locale: Locale;
	readonly lines: readonly GuestIssueLine[];
};

function linesFromQty(
	draftQty: Readonly<Record<string, number>>,
): readonly GuestIssueLine[] {
	return Object.entries(draftQty)
		.filter(([, qty]) => qty > 0)
		.map(([typeId, qty]) => ({ typeId, qty }));
}

@schmancyElement("guest-issue-add")
export class GuestIssueAdd extends LocalizeMixin(SchmancyElement) {
	@property({ type: Boolean }) busy = false;
	@property({ type: Boolean }) settled = false;
	@property({ attribute: false }) failure = "";
	@stateDecorator() private admitted = false;

	@property({ attribute: false }) ticketTypes: readonly SaleTicketType[] = [];
	@property({ attribute: false }) defaultLocale: Locale = "de";
	/** The selected event — schedule/locationId only, already parsed
	 *  (sale.state.ts's own SaleEventOption). Day chips + per-day filtering
	 *  below read only these two fields, the same TicketType.* functions the
	 *  storefront's own programme-day selector reads; a null event (or one
	 *  whose catalog carries no day-bound type) renders exactly today's flat
	 *  list, since no window resolves and no chip has anything to admit on. */
	@property({ attribute: false }) event: SaleEventOption | null = null;

	@stateDecorator() private name = "";
	@stateDecorator() private email = "";
	@stateDecorator() private ticketLocale: Locale | null = null;
	/** Every ticket type is its own row (ticket-type-row.view.ts); this is
	 *  that row list's own qty draft, typeId → qty, absent meaning 0. */
	@stateDecorator() private draftQty: Readonly<Record<string, number>> = {};
	/** The buyer's own day pick — TicketType.resolveProgrammeDay (below)
	 *  heals a null or now-closed pick to the first still-open day. */
	@stateDecorator() private selectedDay: string | null = null;

	override willUpdate(changed: PropertyValues<this>) {
		super.willUpdate(changed);
		if (changed.has("busy") && !this.busy) this.admitted = false;
	}

	override render() {
		const ticketLocale = this.ticketLocale ?? this.defaultLocale;
		const now = Date.now();
		const event = this.event;
		// The operator's own chosen order, the shop's sort exactly
		// (computeShopTypeRows) — an operator reads this catalog in the sequence
		// they dragged it into, never a Firestore snapshot's incidental doc
		// order, and least of all a different sequence on each of the two
		// surfaces that show it. Sorted once here, so both sections and the
		// no-event flat list below inherit it.
		const types = this.ticketTypes.toSorted(
			(a, b) => a.order - b.order || a.name.localeCompare(b.name),
		);
		// Per-type day resolution, mirroring event-page.state.ts's own
		// computeShopTypeRows — the same TicketType.* functions, without the
		// purchasability/basket fields this dialog has no need for. Empty
		// locations map: resolveEffectiveTimezone's own documented fallback
		// (LocationSchema's default timezone) is exact for this deployment's
		// single-venue catalog.
		const typeDayInfo = event
			? types.map((t) => {
					const entry = TicketType.resolveEntryWindow(t, event);
					const timezone = TicketType.resolveEffectiveTimezone(t, event, {});
					const window = {
						opensAt: entry.opensAt.toMillis(),
						closesAt: entry.closesAt.toMillis(),
					};
					return {
						type: t,
						coversWholeEvent: TicketType.coversWholeEvent(window, event),
						days: TicketType.daysCovered(window, timezone),
						window,
						timezone,
					};
				})
			: [];
		const programmeDays = TicketType.programmeDaysOf(
			typeDayInfo
				.filter((r) => !r.coversWholeEvent)
				.map((r) => ({ ...r.window, timezone: r.timezone })),
		).filter((d) => d.closesAt >= now);
		const selectedDay =
			programmeDays.length > 0
				? TicketType.resolveProgrammeDay(programmeDays, this.selectedDay, now)
				: null;
		// The same two sets the shop's own event page splits its catalog into
		// (event-page.state.ts's wholeEventRows/selectedDayRows), predicate for
		// predicate: a type tied to no single day stands once above the chips,
		// and the list beneath holds only what differs per day. Where there are
		// no days to be outside of, nothing is above the chips and every type is
		// simply a row — the one flat list this dialog has always rendered.
		const wholeEventTypes = selectedDay
			? typeDayInfo.filter((r) => r.coversWholeEvent).map((r) => r.type)
			: [];
		const dayTypes = selectedDay
			? typeDayInfo
					.filter((r) => !r.coversWholeEvent && r.days.includes(selectedDay))
					.map((r) => r.type)
			: types;
		const lines = linesFromQty(this.draftQty);
		const totalQty = lines.reduce((sum, l) => sum + l.qty, 0);
		// The days this draft already holds a line for — the storefront's
		// basketProgrammeDays, read off the qty draft instead of the basket. A
		// type covering the whole event is tied to no single day and dots
		// nothing, exactly as it dots nothing in the shop.
		const daysInDraft = new Set(
			typeDayInfo
				.filter(
					(r) => !r.coversWholeEvent && (this.draftQty[r.type.id] ?? 0) > 0,
				)
				.flatMap((r) => r.days),
		);
		const atCap = totalQty >= Event.MAX_TICKETS_PER_ORDER;
		const valid =
			this.name.trim().length > 0 &&
			// The SAME validator BuyerContactSchema.email enforces at the write
			// (issueGuestTicket parses the whole checkout through
			// Checkout.CheckoutSchema) — a looser hand-rolled regex here would
			// pass a value the write then throws on.
			Checkout.BuyerContactSchema.shape.email.safeParse(this.email.trim())
				.success &&
			guestIssueLinesValid(lines);
		const issue = (): void => {
			if (!valid || this.busy || this.admitted || this.settled) return;
			this.admitted = true;
			this.dispatchEvent(
				new CustomEvent<GuestAddDetail>("issue", {
					bubbles: true,
					composed: true,
					detail: {
						name: this.name.trim(),
						email: this.email.trim(),
						locale: ticketLocale,
						lines,
					},
				}),
			);
		};

		return html`
			<!-- Bounded and scrolled here, not by the overlay: thirty ticket types
			     grow this column past any viewport, and an overlay centres what it
			     is given, so an unbounded column puts its own top off-screen and
			     out of reach. -->
			<div ${overflowWithin()} class="flex max-h-[85vh] w-full max-w-md flex-col gap-4 py-5">
				<!-- Above the chips, where the shop's own event page stands it: a
				     type tied to no single day reads the same under every one of
				     them, so it stands once here instead of repeating inside each
				     day's list. Its own px-5, since the chip row between the two
				     sections is full-bleed. Empty means absent — a heading over no
				     rows announces a section that is not there, and the gated box
				     also spares the root column a flex gap for it. -->
				${when(
					wholeEventTypes.length > 0,
					() => html`
						<div class="flex flex-col gap-2 px-5">
							<schmancy-typography type="label" token="sm" transform="uppercase" class="tracking-widest text-surface-onVariant">
								${this.t("storefront.typeSelector.wholeEventTitle")}
							</schmancy-typography>
							${repeat(
								wholeEventTypes,
								(t) => t.id,
								(t) => this.ticketRow(t, totalQty, atCap),
							)}
						</div>
					`,
				)}
				${when(
					programmeDays.length > 0,
					() => html`
						<programme-day-selector
                            ?inert=${this.busy || this.admitted || this.settled}
							inline-padding="px-5"
							.days=${programmeDays}
							.value=${selectedDay}
							.inOrder=${daysInDraft}
							@change=${(e: CustomEvent<{ value: string }>) => {
								if (this.busy || this.admitted || this.settled) return;
								this.selectedDay = e.detail.value;
							}}
						></programme-day-selector>
					`,
				)}
				<!-- The day's own list, and the one cap band the whole draft gets:
				     the ceiling is on the total, not on either section, so it is
				     named once, at the bottom of the rows, where the last increment
				     that went quiet was. -->
				<div class="flex flex-col gap-2 px-5">
					${repeat(
						dayTypes,
						(t) => t.id,
						(t) => this.ticketRow(t, totalQty, atCap),
					)}
					${when(
						atCap,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("admin.sale.guestIssue.capReached", {
									cap: Event.MAX_TICKETS_PER_ORDER,
								})}
							</schmancy-typography>
						`,
					)}
				</div>

				<div class="grid grid-cols-2 gap-2 px-5">
					<schmancy-input
                        ?readonly=${this.busy || this.admitted || this.settled}
						label=${this.t("admin.sale.guestIssue.nameLabel")}
						type="text"
						autocomplete="name"
						required
						.value=${this.name}
						.errorMessages=${{
							valueMissing: this.t("admin.sale.guestIssue.nameRequiredError"),
						}}
						@input=${(e: CustomEvent<{ value: string }>) => {
							if (this.busy || this.admitted || this.settled) return;
							this.name = e.detail.value;
						}}
					></schmancy-input>
					<schmancy-input
                        ?readonly=${this.busy || this.admitted || this.settled}
						label=${this.t("admin.sale.guestIssue.emailLabel")}
						type="email"
						autocomplete="email"
						required
						placeholder=${this.t("admin.sale.guestIssue.emailPlaceholder")}
						.value=${this.email}
						.errorMessages=${{
							valueMissing: this.t("admin.sale.guestIssue.emailRequiredError"),
							typeMismatch: this.t("admin.sale.guestIssue.emailInvalidError"),
						}}
						@input=${(e: CustomEvent<{ value: string }>) => {
							if (this.busy || this.admitted || this.settled) return;
							this.email = e.detail.value;
						}}
						@enter=${issue}
					></schmancy-input>
				</div>

				<div class="flex flex-col gap-1 px-5">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.sale.guestIssue.localeLabel")}
					</schmancy-typography>
					<schmancy-chips
                        ?inert=${this.busy || this.admitted || this.settled}
						required
						.value=${ticketLocale}
						@change=${(e: CustomEvent<{ value: string | string[] }>) => {
							if (this.busy || this.admitted || this.settled) return;
							const v = e.detail.value;
							const parsed = LocaleEnum.safeParse(
								typeof v === "string" ? v : (v[0] ?? ""),
							);
							if (!parsed.success || parsed.data === ticketLocale) return;
							this.ticketLocale = parsed.data;
						}}
					>
						${repeat(
							LocaleEnum.options,
							(l) => l,
							(l) =>
								html`<schmancy-filter-chip value=${l}>${LOCALE_CHIP_LABEL[l]}</schmancy-filter-chip>`,
						)}
					</schmancy-chips>
				</div>

				<schmancy-button class="px-5" variant="filled" width="full" .busy=${this.busy || this.admitted}
                        ?disabled=${!valid || this.settled} @click=${issue}>
					${this.t("admin.sale.guestIssue.submitButton")}
				</schmancy-button>
                <schmancy-typography role="status" type="body" token="sm" class="h-12 px-5">
                    ${this.failure || (this.settled ? this.t("admin.sale.guestIssue.settledBody", { name: this.name, email: this.email }) : "")}
                </schmancy-typography>
			</div>
		`;
	}

	/** One row, resolved identically in both sections — the whole-event group
	 *  above the chips and the day's list beneath them render the same ticket,
	 *  and a row that read differently in one of them would be a second ticket
	 *  type from the operator's side. `totalQty` is the whole draft's, never
	 *  the section's: the ceiling is on the order, so the headroom a `+`
	 *  measures against is what every other row already holds. */
	private ticketRow(t: SaleTicketType, totalQty: number, atCap: boolean) {
		const qty = this.draftQty[t.id] ?? 0;
		const typeName = resolveLocalizedText(
			t.nameByLocale,
			t.name,
			this.uiLanguage,
		);
		const setQty = (nextQty: number): void => {
			if (this.busy || this.admitted || this.settled) return;
			const fitted = fitGuestIssueQty(nextQty, totalQty - qty);
			const next = { ...this.draftQty };
			if (fitted <= 0) delete next[t.id];
			else next[t.id] = fitted;
			this.draftQty = next;
		};
		const row: TicketTypeRow = {
			id: t.id,
			name: typeName,
			description: resolveLocalizedText(
				t.descriptionByLocale,
				t.description,
				this.uiLanguage,
			),
			priceLabel:
				t.price === 0
					? this.t("admin.sale.guestIssue.freeLabel")
					: formatCurrency(t.price / 100, null),
			hints: [],
			selected: qty > 0,
			receded: false,
			clickable: !atCap && !this.busy && !this.admitted && !this.settled,
			rowAriaLabel: null,
			nameButtonAriaLabel: this.t(
				"admin.sale.guestIssue.typeRowNameAriaLabel",
				{ name: t.name },
			),
			descriptionButtonAriaLabel: this.t(
				"admin.sale.guestIssue.typeRowDescriptionAriaLabel",
				{ name: typeName },
			),
			descriptionBackLabel: this.t(
				"admin.sale.guestIssue.typeRowDescriptionBackLabel",
			),
			slot: {
				kind: "stepper",
				qty,
				canIncrement: !atCap && !this.busy && !this.admitted && !this.settled,
				canDecrement: qty > 0 && !this.busy && !this.admitted && !this.settled,
				incrementAriaLabel: this.t("admin.sale.guestIssue.incrementAriaLabel", {
					name: typeName,
				}),
				decrementAriaLabel: this.t("admin.sale.guestIssue.decrementAriaLabel", {
					name: typeName,
				}),
				qtyField: {
					ariaLabel: this.t("admin.sale.guestIssue.qtyFieldAriaLabel", {
						name: typeName,
					}),
				},
			},
		};
		return html`
			<ticket-type-row
                ?inert=${this.busy || this.admitted || this.settled}
				.row=${row}
				@increment=${() => setQty(qty + 1)}
				@decrement=${() => setQty(qty - 1)}
				@qtyEntered=${(e: CustomEvent<{ value: string }>) => {
					const typed = e.detail.value.trim();
					// A cleared field and a typed 0 are one intent — not selected — so
					// both land where − already lands. Anything above zero goes through
					// the write rule's own qty >= 1 floor.
					setQty(
						typed === "" || Number.parseInt(typed, 10) <= 0
							? 0
							: clampGuestIssueQty(typed),
					);
				}}
			></ticket-type-row>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"guest-issue-add": GuestIssueAdd;
	}
}
